/* eslint-disable @typescript-eslint/no-explicit-any */
/*
Messages should not contain any sensitive information.
Store sensitive information in storage (localStorage, sessionStorage, cookie and/or indexedDb) and send the key in the message.
 */
// Order: RULEBOOK §5.3 (dependency-safe; do not reorder listeners/subscriptions above their stores).
import { isRakeboostBoosted, isRakeboostFinished, rakeboostEndAt, rakeboostProgress, rakeboostTimeRemaining, refreshVipState } from './dataStore';
import { derived, get, writable } from 'svelte/store';
import { getBehaviorSubject } from './rxjs-utils';
// Constants
/** `postMessage` target; paired with `onMessage` registered on `window` from `@infinitro/global` index. */
const TARGET = '*';
// const TARGET = window.location.origin;
/** Width breakpoints for `isMobile` / `isTablet` and layout edge cases (`isSpecialWidthCase`, sidebars). */
const MOBILE_MAX_WIDTH = 1024;
const TABLET_MIN_WIDTH = 640;
const TABLET_MAX_WIDTH = 1024;
const SPECIAL_WIDTH_MIN_WIDTH = 1280;
const SPECIAL_WIDTH_MAX_WIDTH = 1440;
const MIN_FULL_HD_WIDTH = 1920;
/** UA sniff helper (see `detectMobileUA`); not the primary source of truth vs width-based `isMobile`. */
const MOBILE_UA_REGEX = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
/** Default seconds for `startUiTimer` (promos / gated UI countdowns). */
export const DEFAULT_UI_TIMER_SECONDS = 30;
/** Rakeboost smooth progress bar refresh interval (ms). */
const timerSpeed = 100;
// Enums
/**
 * Discriminant on `postMessage` payloads (`data.ui`): iframe ↔ parent sync (fullscreen, cashier shells),
 * and `UI.State` for session-ish messages. Consumed in player/public layouts and global `onMessage`.
 */
export var UI;
(function (UI) {
    UI["Auth"] = "auth";
    UI["BuyCrypto"] = "buyCrypto";
    UI["Cashier"] = "cashier";
    UI["FullScreen"] = "fullScreen";
    UI["Geoblock"] = "geoblock";
    UI["Login"] = "login";
    UI["Maintenance"] = "maintenance";
    UI["Freebets"] = "freebets";
    UI["Quest"] = "quest";
    UI["Search"] = "search";
    UI["Settings"] = "settings";
    UI["Signup"] = "signup";
    UI["Spin"] = "spin";
    UI["State"] = "state";
    UI["Support"] = "support";
})(UI || (UI = {}));
/**
 * Modal ids for `stateOpenModal`, `modalStack`, and stacked modal host (`player/modals.svelte`, rewards,
 * auth, dev settings, etc.). `None` = closed sentinel in legacy tuple store.
 */
export var ModalDialog;
(function (ModalDialog) {
    ModalDialog["AccountMerge"] = "accountMerge";
    ModalDialog["AddEmail"] = "addEmail";
    ModalDialog["AddPhone"] = "addPhone";
    ModalDialog["Alias"] = "alias";
    ModalDialog["AllRanks"] = "allRanks";
    ModalDialog["Auth"] = "auth";
    ModalDialog["BetSlip"] = "betSlip";
    ModalDialog["Calendar"] = "calendar";
    ModalDialog["Cashier"] = "cashier";
    ModalDialog["CubePay"] = "cubePay";
    ModalDialog["ChatRules"] = "chatRules";
    ModalDialog["ChristmasModal"] = "christmasModal";
    ModalDialog["Confirmation"] = "confirmation";
    ModalDialog["DevSettings"] = "devSettings";
    ModalDialog["EmailVerify"] = "emailVerify";
    ModalDialog["Error"] = "error";
    ModalDialog["FeatureUnavailable"] = "featureUnavailable";
    ModalDialog["Freebets"] = "freebets";
    ModalDialog["GameInfo"] = "gameInfo";
    ModalDialog["Geoblock"] = "geoblock";
    ModalDialog["Loading"] = "loading";
    ModalDialog["Login"] = "login";
    ModalDialog["LoginViaWallet"] = "loginViaWallet";
    ModalDialog["LuckyDip"] = "luckyDip";
    ModalDialog["Maintenance"] = "maintenance";
    ModalDialog["NoCashback"] = "noCashback";
    ModalDialog["None"] = "none";
    ModalDialog["Notifications"] = "notifications";
    ModalDialog["OtherProfile"] = "otherProfile";
    ModalDialog["PhoneVerify"] = "phoneVerify";
    ModalDialog["ProfileDropdown"] = "profileDropdown";
    ModalDialog["ProfileModal"] = "profileModal";
    ModalDialog["RacesLeaderboard"] = "racesLeaderboard";
    ModalDialog["Rain"] = "rain";
    ModalDialog["Rakeback"] = "rakeback";
    ModalDialog["RakeboostDropdown"] = "rakeboostDropdown";
    ModalDialog["ResetCashback"] = "resetCashback";
    ModalDialog["RewardsDetails"] = "rewardsDetails";
    ModalDialog["RewardsDropdown"] = "rewardsDropdown";
    ModalDialog["Search"] = "search";
    ModalDialog["Settings"] = "settings";
    ModalDialog["ShareViaSocials"] = "shareViaSocials";
    ModalDialog["Signup"] = "signup";
    ModalDialog["Tip"] = "tip";
    ModalDialog["TotpSetup"] = "totpSetup";
    ModalDialog["TotpVerify"] = "totpVerify";
    ModalDialog["Update"] = "update";
    ModalDialog["VipClub"] = "vipClub";
    ModalDialog["VipPendingRewards"] = "vipPendingRewards";
    ModalDialog["WalletDropdown"] = "walletDropdown";
    ModalDialog["WalletManager"] = "walletManager";
    ModalDialog["WalletVerify"] = "walletVerify";
    ModalDialog["WelcomeModal"] = "welcomeModal";
})(ModalDialog || (ModalDialog = {}));
/** `data.state` on `MessageState` postMessages (login/logout/lang/hard reload) — see `subscribeMessageState`. */
export var State;
(function (State) {
    State["HardReload"] = "hardReload";
    State["Language"] = "language";
    State["LoggedIn"] = "loggedIn";
    State["LoggedOut"] = "loggedOut";
})(State || (State = {}));
// postMessage
/** Debug label: this window vs its parent chain (used in `onMessage` logs). */
export const getRelation = () => {
    if (window === window.parent)
        return 'Parent';
    else if (window.parent !== window.parent.parent)
        return 'GrandChild';
    return 'Child';
};
/** Debug label: `MessageEvent.source` vs this window (used in `onMessage` logs). */
export const getSourceRelation = (source) => {
    if (source === window)
        return 'Sibling';
    if (source === window.parent)
        return 'Parent';
    if (source === window.parent.parent)
        return 'GrandParent';
    if (source === window.parent.parent)
        return 'GreatGrandParent';
    return 'Child';
};
/**
 * Global handler registered in `@infinitro/global` index: logs, optional fullscreen branch, bubbles
 * unknown-child messages to `parent` when nested.
 */
export const onMessage = (event) => {
    if (!event?.data?.ui)
        return;
    console.log(`received ${JSON.stringify(event?.data)} by ${getRelation()} from ${getSourceRelation(event.source)}`, event);
    try {
        if (event.data.ui === UI.FullScreen && event.source !== window) {
            console.log(`setting isFullScreenGame to ${event.data.value} from message`);
            // Only originals use this and it will not be neccesary anymore
            // if (event.data.value === true) {
            // 	isFullScreenGame.set(true);
            // } else if (event.data.value === false) {
            // 	isFullScreenGame.set(false);
            // }
        }
    }
    catch (e) {
        console.log(e);
    }
    // propogate message up to parent
    if (window !== window.parent && event.source !== window.parent)
        window.parent.postMessage(event?.data, TARGET);
};
/** Fire-and-forget UI message on this browsing context; fills `ui` with `UI.State` if missing. */
export const sendMessage = (message) => {
    if (!message.ui)
        message.ui = UI.State;
    window.postMessage(message, TARGET);
};
/** Display payload only; caller must set `ui` (no default here). */
export const sendMessageDisplay = (message) => {
    window.postMessage(message, TARGET);
};
/** Session-style payload; fills `ui` with `UI.State` if missing. */
export const sendMessageState = (message) => {
    if (!message.ui)
        message.ui = UI.State;
    window.postMessage(message, TARGET);
};
/** Host-side: forward parent/window messages into an embedded iframe (skips echo from the frame). */
export const relayMessage = (frame, event) => {
    if (event.source === frame.contentWindow)
        return;
    frame.contentWindow?.postMessage(event.data, TARGET);
};
/** Attach relay listener for one iframe (e.g. cashier test frame, embedded flows). */
export const setupMessageRelay = (frame) => {
    window.addEventListener('message', (event) => relayMessage(frame, event));
};
/** Subscribe to all UI-tagged messages (broad; prefer display/state variants when possible). */
export const subscribeMessage = (callback) => {
    window.addEventListener('message', (event) => {
        if (event?.data?.ui)
            callback(event.data, event);
    });
};
/** Subscribe to session postMessages (cashier / shell integration; see commented usage in cashier4 state). */
export const subscribeMessageState = (callback) => {
    window.addEventListener('message', (event) => {
        if (event?.data?.ui && event?.data?.state)
            callback(event.data, event);
    });
};
/** Subscribe to surface modals/overlays traffic (excludes `UI.State` channel). */
export const subscribeMessageDisplay = (callback) => {
    window.addEventListener('message', (event) => {
        if (event?.data?.ui && event?.data.ui !== UI.State)
            callback(event.data, event);
    });
};
/** Privacy / reveal toggles (mask sensitive strings in forms or summaries). */
export const maskText = (text, maskChar, applyMask) => applyMask ? maskChar.repeat(text.length) : text;
// Viewport (call detectMobileOS() only after isMobile — see stores block)
/** Raw UA test against `MOBILE_UA_REGEX` (supplemental; width drives `isMobile`). */
function detectMobileUA() {
    return MOBILE_UA_REGEX.test(navigator.userAgent);
}
/** Viewport below mobile breakpoint (used for responsive layout across libs). */
function calcIsMobile() {
    if (typeof window === 'undefined')
        return false;
    return window.innerWidth < MOBILE_MAX_WIDTH;
}
/** Tablet band between mobile and desktop widths. */
function calcIsTablet() {
    if (typeof window === 'undefined')
        return false;
    return window.innerWidth >= TABLET_MIN_WIDTH && window.innerWidth < TABLET_MAX_WIDTH;
}
/** Portrait: width smaller than height (mobile chrome / orientation hints). */
function calcPortrait() {
    if (typeof window === 'undefined')
        return false;
    return window.innerWidth < window.innerHeight;
}
/** Resize/orientation handler: keeps `isMobile` / `isTablet` / `isPortrait` writables in sync. */
function updateScreenSize() {
    isMobile.set(calcIsMobile());
    isPortrait.set(calcPortrait());
    isTablet.set(calcIsTablet());
}
/** Derives `isAndroid` / `isIOS` initial flags; gated on `get(isMobile)` so desktop UA does not misclassify. */
function detectMobileOS() {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
        return { isAndroidDevice: false, isIOSDevice: false };
    }
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroidDevice = /android/i.test(ua) && get(isMobile);
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window) && get(isMobile);
    return { isAndroidDevice, isIOSDevice };
}
// Stores — general
/** Which DOM node owns the shared tooltip (e.g. bet-feed `tooltip-info`). */
export const activeTooltip = writable(null);
/** Scroll position snapshot for layouts restoring scroll. */
export const scrollPosition = writable(0);
/** Player layout: gate scroll-to-top on navigation when true. */
export const shouldScrollToTop = writable(true);
/** Props for calendar modal content (advent / promos); set via `setCalendarModalContent`. */
export const calendarModalContent = writable(null);
/** Verified email string mirrored for UI (profile / KYC flows). */
export const email = writable('');
/** Public alias / nickname for UI. */
export const alias = writable('');
/** Footer / compliance: show license seal imagery. */
export const stateShowSeal = writable(false);
/** Footer: show seal caption/text variants. */
export const stateShowSealText = writable(false);
/** Footer: extra geo-block strip under the fold. */
export const stateShowGeoBlockFooter = writable(false);
/** Footer: geo-block copy label (regulatory messaging). */
export const stateGeoBlockLabel = writable('');
// Stores — layout
/** Desktop sidebar collapsed state (drives `isSpecialWidthCase`, `isChatAndSidebarOpened`). */
export const sidebarCollapsed = writable(false);
/** Mobile drawer: sidebar visibility. */
export const stateShowSidebarMobile = writable(false);
/** Right-rail notifications panel open (with chat, sets `layoutRightOffset`). */
export const isNotificationsOpen = writable(false);
/** Player chat panel open (layout offset + width breakpoints). */
export const isPlayerChatOpen = writable(false);
/** Pixel offset for main content when chat or notifications panel reserves right rail. */
export const layoutRightOffset = derived([isPlayerChatOpen, isNotificationsOpen], ([$isPlayerChatOpen, $isNotificationsOpen]) => $isPlayerChatOpen ? 240 : $isNotificationsOpen ? 240 : 0);
/** Mobile bottom/slide menu active section (sports, casino, chat, etc.). */
export const selectedItemMobileMenu = writable(null);
// Stores — modals (legacy + stack)
/** Legacy “single open modal” id; still synced with stack in places (see `closeThisModal`). */
export const stateOpenModal = writable(undefined);
/** Legacy tuple `[modal, payload]`; cleared when `stateOpenModal` clears. */
export const stateOpenModalData = writable([ModalDialog.None, undefined]);
/** Snapshot array for non-reactive helpers (`isModalOpen`); store value is source of truth for UI. */
let _modalStack = [];
/** Ordered modal stack for `player/modals.svelte` and stacked UX (push/pop/replace). */
export const modalStack = writable(_modalStack);
/** Push modal on stack or bring same `modal` to top with new `data`. */
export const pushModal = (modal, data) => {
    modalStack.update((stack) => {
        const index = stack.findIndex((e) => e.modal === modal);
        if (index >= 0) {
            // at least presently can't stack the same modal twice, but can bring it to the top
            if (index === stack.length - 1) {
                // Already on top — still replace its data, otherwise a re-push with fresh
                // payload (e.g. newly fetched prefill data/callbacks) is silently dropped.
                return [...stack.slice(0, index), { modal, data }];
            }
            return [
                ...stack.slice(0, index),
                ...stack.slice(index + 1),
                { modal, data }
            ]; /* bring the modal to the top */
        }
        return [...stack, { modal, data }]; /* add the new modal to the end of the stack (top) */
    });
};
/** Pop top modal; if `modal` passed, only pops when that id is currently top. */
export const popModal = (modal) => {
    modalStack.update((stack) => {
        if (stack.length === 0)
            return stack;
        if (modal && stack[stack.length - 1].modal !== modal) {
            console.log('popModal not on top', modal, stack);
            return stack;
        }
        return stack.slice(0, -1);
    });
};
/** Remove one modal everywhere (stack + legacy `stateOpenModal*` when it matches). */
export const closeThisModal = (modal) => {
    modalStack.update((stack) => {
        return stack.filter((e) => e.modal !== modal);
    });
    stateOpenModalData.update((value) => value?.[0] === modal ? [ModalDialog.None, undefined] : value);
    stateOpenModal.update((value) => (value === modal ? undefined : value));
};
/** Close all stacked modals (does not alone reset every legacy field — callers may still set `stateOpenModal`). */
export const clearModals = () => {
    modalStack.set([]);
};
/** Replace stack with a single modal (full-screen takeover flows). */
export const replaceModal = (modal, data) => {
    modalStack.set([{ modal, data }]);
};
/** Replace entire stack; tuple typing matches historical call sites (see usages before changing). */
export const replaceModalStack = (stack) => {
    modalStack.set(stack);
};
/** Open if absent, close if present (toggle entry). */
export const toggleModal = (modal, data) => {
    modalStack.update((stack) => {
        const index = stack.findIndex((e) => e.modal === modal);
        if (index === -1) {
            return [...stack, { modal, data }];
        }
        else {
            return stack.filter((e) => e.modal !== modal);
        }
    });
};
/**
 * Imperative “is this id in stack?” — reads `_modalStack` snapshot only; in Svelte prefer
 * `derived(modalStack, ...)`.
 */
export const isModalOpen = (modal) => {
    if (_modalStack.length === 0)
        return false;
    return _modalStack.some((e) => e.modal === modal);
};
/** Snapshot “is top?”; prefer `derived(modalStack)` in components. */
export const isModalTop = (modal) => {
    return _modalStack.length > 0 && _modalStack[_modalStack.length - 1].modal === modal;
};
/** Snapshot payload lookup; prefer `derived(modalStack)` in components. */
export const modalPayload = (modal) => {
    return _modalStack.find((e) => e.modal === modal)?.data;
};
/** Legacy: clearing `stateOpenModal` resets `stateOpenModalData` tuple to `None`. */
stateOpenModal.subscribe((value) => {
    if (!value || value === ModalDialog.None) {
        stateOpenModalData.set([ModalDialog.None, undefined]);
        // modalStack.set([]);
    }
});
// Stores — loading / bonuses / game / viewport
/** Global loading chrome (spinners, blocking overlays). */
export const showLoading = writable(false);
/** Trigger profile refetch after auth or wallet updates. */
export const fetchProfileData = writable(false);
/** Deposit-offer pre-accept token/id for cashier flows. */
export const stateDepositBonusPreaccepted = writable(undefined);
/** Per-offer accept/decline map for rewards/cashier UI. */
export const stateDepositBonusStatus = writable({});
/** Game info modal payload (casino game meta). */
export const selectedGameInfo = writable(undefined);
/** Search modal: which tab to open by default. */
export const searchModalPreselectedTab = writable(undefined);
/** Sportsbook lazy-load gate (sidebar / races). */
export const hasSportsLoaded = writable(false);
/** Demo mode active (game iframe / analytics behavior). */
export const isDemoGamePlaying = writable(false);
/**
 * Game iframe fullscreen chrome; child frames notify parent via `postMessage` subscription below;
 * public game routes read this for layout.
 */
export const isFullScreenGame = writable(false);
/** Cinematic/minimal chrome mode on game pages (public routes). */
export const isMovieModeGame = writable(false);
/** Any real-money or demo game session active (global chrome). */
export const isGamePlaying = writable(false);
/** Count of standalone (non-modal) forms currently focused/filled — see `registerActiveForm`. */
export const activeFormsCount = writable(0);
/** True while any standalone form is focused/filled (`safeToReload` input). */
export const isFormActive = derived(activeFormsCount, (n) => n > 0);
/** Call on focus/content-start, call the returned fn on blur/content-clear/destroy — never both twice. */
export const registerActiveForm = () => {
    activeFormsCount.update((n) => n + 1);
    let released = false;
    return () => {
        if (released)
            return;
        released = true;
        activeFormsCount.update((n) => Math.max(0, n - 1));
    };
};
/** Width-based mobile flag — layouts, modals, BetBy, chat, bet-feed, etc. */
export const isMobile = writable(calcIsMobile());
/** Width-based tablet band. */
export const isTablet = writable(calcIsTablet());
/** Orientation / aspect helper. */
export const isPortrait = writable(calcPortrait());
/** Layout tweak when chat + mid-width viewport + expanded sidebar (player shell). */
export const isSpecialWidthCase = derived([isPlayerChatOpen, sidebarCollapsed], ([$isPlayerChatOpen, $sidebarCollapsed]) => typeof window !== 'undefined' &&
    $isPlayerChatOpen &&
    window.innerWidth >= SPECIAL_WIDTH_MIN_WIDTH &&
    window.innerWidth < SPECIAL_WIDTH_MAX_WIDTH &&
    !$sidebarCollapsed);
/** Chat open while sidebar expanded (layout overlap rules). */
export const isChatAndSidebarOpened = derived([isPlayerChatOpen, sidebarCollapsed], ([$isPlayerChatOpen, $sidebarCollapsed]) => $isPlayerChatOpen && !$sidebarCollapsed);
/** Initial module-load width snapshot for “special width” band (not reactive on resize). */
export const isSmallMonitor = typeof window !== 'undefined' &&
    window.innerWidth >= SPECIAL_WIDTH_MIN_WIDTH &&
    window.innerWidth < SPECIAL_WIDTH_MAX_WIDTH;
/** Tracks ≥ Full HD width; updated at init (writable for future resize hooks if added). */
export const isFullHDMonitor = writable(typeof window !== 'undefined' && window.innerWidth >= MIN_FULL_HD_WIDTH);
const { isAndroidDevice, isIOSDevice } = detectMobileOS();
/** Native-app-style flags; use with PWA / download prompts when `isMobile`. */
export const isAndroid = writable(isAndroidDevice);
export const isIOS = writable(isIOSDevice);
/** Social / rain / profile surfaces: which user profile is focused. */
export const selectedProfile = writable(null);
/** Sports/casino bet slip attachment for modals. */
export const selectedBetSlip = writable(null);
/** Tip flow in progress (chat / wallet). */
export const sendingTip = writable(false);
/** Crypto deposit flow busy (rewards dropdown / cashier). */
export const depositingCrypto = writable(false);
/** Cash deposit flow busy (rewards dropdown / cashier). */
export const depositingCash = writable(false);
/** Optional iframe src for staged embedded flows (player/public/min layouts, Gamma gate). */
export const stateIFrameHref = writable(undefined);
// UI timer
let _uiTimer = undefined;
/** Total seconds when `startUiTimer` began (for progress UI). */
export const stateUiTimerFrom = writable(0);
/** Ticking seconds remaining; driven by `startUiTimer` interval. */
export const stateUiTimerCount = writable(0);
/** True when countdown reached zero or stopped. */
export const stateUiTimerEnded = derived([stateUiTimerCount], ([$stateUiTimerCount]) => $stateUiTimerCount <= 0);
/** Clears interval and zeroes countdown store. */
const stopUiTimer = () => {
    if (_uiTimer)
        clearInterval(_uiTimer);
    _uiTimer = undefined;
    stateUiTimerCount.set(0);
};
/** Starts 1s tick countdown (promos, rate limits UI); resets if already running. */
export const startUiTimer = (initialTime = DEFAULT_UI_TIMER_SECONDS, remainingTime = initialTime) => {
    console.log(`startUiTimer: ${remainingTime} of ${initialTime} seconds`);
    if (_uiTimer)
        clearInterval(_uiTimer);
    stateUiTimerCount.set(remainingTime);
    stateUiTimerFrom.set(initialTime);
    _uiTimer = setInterval(() => {
        stateUiTimerCount.update((value) => {
            value -= 1;
            if (value <= 0)
                stopUiTimer();
            return value;
        });
    }, 1000);
};
// Instant Cashback REQUEST/RESET rate-limit timer (separate from verification resend).
let _instantCashbackTimer = undefined;
/** Ticking seconds remaining on the Instant Cashback cooldown. */
export const stateInstantCashbackTimerCount = writable(0);
/** True when Instant Cashback cooldown reached zero or is not running. */
export const stateInstantCashbackTimerEnded = derived([stateInstantCashbackTimerCount], ([$stateInstantCashbackTimerCount]) => $stateInstantCashbackTimerCount <= 0);
const stopInstantCashbackTimer = () => {
    if (_instantCashbackTimer)
        clearInterval(_instantCashbackTimer);
    _instantCashbackTimer = undefined;
    stateInstantCashbackTimerCount.set(0);
};
/** Starts the Instant Cashback 30s cooldown; independent of `startUiTimer`. */
export const startInstantCashbackTimer = (initialTime = DEFAULT_UI_TIMER_SECONDS, remainingTime = initialTime) => {
    if (_instantCashbackTimer)
        clearInterval(_instantCashbackTimer);
    stateInstantCashbackTimerCount.set(remainingTime);
    _instantCashbackTimer = setInterval(() => {
        stateInstantCashbackTimerCount.update((value) => {
            value -= 1;
            if (value <= 0)
                stopInstantCashbackTimer();
            return value;
        });
    }, 1000);
};
/**
 * Publishes calendar modal props; each call adds another `subscribe` to the store (existing behavior —
 * avoid hot-loop calls).
 */
export function setCalendarModalContent(modalContent) {
    calendarModalContent.set(modalContent);
    calendarModalContent.subscribe((value) => {
        console.log('setCalendarModalContent', value);
    });
}
// Listeners (after stores)
if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateScreenSize);
    if (window.screen.orientation) {
        window.screen.orientation.addEventListener('change', updateScreenSize);
    }
    else {
        window.addEventListener('orientationchange', updateScreenSize);
    }
}
/** Nested game iframe: notify parent when fullscreen toggles (pairs with `UI.FullScreen` in `onMessage`). */
isFullScreenGame.subscribe((value) => {
    if (typeof window === 'undefined')
        return;
    try {
        console.log(`parent ${window === window.parent} global isFullScreenGame: ${value}`);
        if (window !== window.parent)
            window.parent.postMessage({ value, ui: UI.FullScreen }, '*');
    }
    catch (e) {
        console.log(e);
    }
});
/** Persist email on session/profile updates. */
export const setEmail = (newEmail) => {
    email.set(newEmail);
};
/** Persist alias on profile updates. */
export const setAlias = (newAlias) => {
    alias.set(newAlias);
};
// BetBy / cashier
/** Player/public layout: BetBy sports shell visible (hides default chrome). */
export const isBetbyDisplayed = writable(false);
/** True when no game, sportsbook, modal, or standalone form is in progress — safe to interrupt with an update. */
export const safeToReload = derived([isGamePlaying, isBetbyDisplayed, modalStack, isFormActive], ([$isGamePlaying, $isBetbyDisplayed, $modalStack, $isFormActive]) => !$isGamePlaying && !$isBetbyDisplayed && $modalStack.length === 0 && !$isFormActive);
/** BetBy internal path string for integrations. */
export const stateBetbyPage = writable('');
/** BetBy → app route sync (`betby/frame` and layouts). */
export const betbyCurrentRoute = writable('');
/** Cashier opened from rain promo (downstream analytics / UX). */
export const isCashierOpenedFromRain = writable(false);
/** Remember entry path when deep-linking into a game. */
export const gameLaunchedFromPath = writable();
/** Betslip/transactions lists ask for refresh (BetBy + wallet). */
export const updateBetsTransactions = writable(false);
// VIP rakeboost
/** Denominator ms for `rakeboostProgress` when boost running. */
let countFrom = 0;
/** 1s tick: updates HH:MM:SS string in VIP UI. */
let rakeboostInterval;
/** Fast tick: smooths progress bar toward `rakeboostEndAt`. */
let rakeboostFastTimerInterval;
/** High-frequency progress fill from `rakeboostEndAt` vs `countFrom`. */
function fastCountdown() {
    if (!get(rakeboostEndAt) && get(rakeboostEndAt) === undefined) {
        if (rakeboostFastTimerInterval)
            clearInterval(rakeboostFastTimerInterval);
        return;
    }
    const diff = get(rakeboostEndAt).getTime() - Date.now();
    if (diff > 0) {
        rakeboostProgress.set((diff / countFrom) * 100);
    }
    else {
        rakeboostProgress.set(0);
    }
}
/** Arms intervals and boosted flag when countdown should run. */
function startRakeboost(diff) {
    console.log(`startRakeboost: ${diff}`);
    isRakeboostBoosted.set(true);
    countFrom = diff ?? 0;
    if (!rakeboostInterval) {
        rakeboostInterval = setInterval(updateCountdownRakeboost, 1000);
    }
    if (!rakeboostFastTimerInterval) {
        rakeboostFastTimerInterval = setInterval(fastCountdown, timerSpeed);
    }
}
/** Teardown timers, reset VIP stores, refresh VIP query. */
function endRakeboost() {
    console.log(`endRakeboost`);
    clearRakeboostIntervals();
    rakeboostTimeRemaining.set('00:00:00');
    isRakeboostFinished.set(true);
    isRakeboostBoosted.set(false);
    refreshVipState();
}
/**
 * VIP rakeboost countdown driver (data from `dataStore`); call with `setupTimer` when starting a new boost window.
 */
export function updateCountdownRakeboost(setupTimer = false) {
    try {
        if (!get(rakeboostEndAt) || get(rakeboostEndAt) === undefined) {
            endRakeboost();
            return;
        }
        const diff = get(rakeboostEndAt).getTime() - Date.now();
        if (diff > 0) {
            const hours = Math.floor(diff / (1000 * 60 * 60)) % 24;
            const minutes = Math.floor(diff / (1000 * 60)) % 60;
            const seconds = Math.floor(diff / 1000) % 60;
            rakeboostTimeRemaining.set(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
            if (setupTimer) {
                startRakeboost(diff);
            }
            else {
                // progress = (diff / countFrom) * 100;
            }
        }
        else {
            // progress = 0;
            endRakeboost();
        }
    }
    catch (e) {
        console.log(`updateCountdown exception: ${e}`);
    }
}
/** Stop both rakeboost intervals (logout, boost end, unmount guards). */
export function clearRakeboostIntervals() {
    if (rakeboostInterval) {
        clearInterval(rakeboostInterval);
        rakeboostInterval = undefined;
    }
    if (rakeboostFastTimerInterval) {
        clearInterval(rakeboostFastTimerInterval);
        rakeboostFastTimerInterval = undefined;
    }
}
// Copy / i18n
/** Strip `[brackets]` from CMS copy while keeping inner text (translation pipeline). */
export function cleanStringTranslation(text) {
    if (!text)
        return '';
    return text.replace(/\[([^\]]+)\]/g, '$1');
}
/** Turn `[phrase]` segments into highlighted HTML (promo banners, rich descriptions). */
export function formatWithHighlight(text) {
    if (!text)
        return '';
    return text.replace(/\[([^\]]+)\]/g, '<span style="color: var(--top-section-main-title); font-weight: 600;">$1</span>');
}
/** English ordinal suffix for dates (`1st`, `2nd`, …). */
export function enSuffix(n) {
    const v = n % 100;
    if (v >= 11 && v <= 13)
        return 'th';
    switch (n % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
}
/** French ordinal for calendar-style labels (`1er`, `2e`). */
export function frSuffix(n) {
    return n === 1 ? 'er' : 'e';
}
/** Turkish ordinal label for deposit/bonus titles (`İlk`, `2.`, `3.`, …). */
export function trOrdinal(n) {
    if (n === 1)
        return 'İlk';
    return `${n}.`;
}
// Colors
/** Resolve `var(--token)` to computed value on `el` (theme tokens → hex/rgb). */
function resolveColorInput(input, el = document.documentElement) {
    input = input.trim();
    // var(--token[, fallback])
    if (input.startsWith('var(')) {
        const m = input.match(/var\(\s*(--[A-Za-z0-9-_]+)\s*(?:,\s*([^)]+))?\)/);
        if (!m)
            return null;
        const [, token, fallback] = m;
        let value = getComputedStyle(el).getPropertyValue(token).trim();
        if (!value && fallback)
            value = fallback.trim();
        return value || null;
    }
    return input || null;
}
/** Parse `#RGB`/`#RRGGBB`/`#RRGGBBAA` into components. */
export function hexToRgb(hex) {
    hex = hex.trim().replace(/^#/, '');
    if (![3, 4, 6, 8].includes(hex.length))
        return null;
    // Expand #RGB / #RGBA to #RRGGBB / #RRGGBBAA
    if (hex.length === 3 || hex.length === 4) {
        hex = hex
            .split('')
            .map((ch) => ch + ch)
            .join('');
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = hex.length === 8 ? +(parseInt(hex.slice(6, 8), 16) / 255).toFixed(4) : undefined;
    return { r, g, b, ...(a !== undefined ? { a } : {}) };
}
/** Build `r, g, b` string for `rgb()` from hex, CSS var, or existing `rgb()` string. */
export function toRgbBase(input, el = document.documentElement) {
    const resolved = resolveColorInput(input, el);
    if (!resolved)
        return null;
    // If it's already rgb(a), parse it directly
    if (resolved.startsWith('rgb')) {
        const nums = resolved.match(/[\d.]+/g);
        if (!nums)
            return null;
        const [r, g, b] = nums.map((n) => Math.round(parseFloat(n)));
        return `${r}, ${g}, ${b}`;
    }
    // HEX case
    if (resolved.startsWith('#')) {
        const rgb = hexToRgb(resolved);
        return rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : null;
    }
    // Other formats (e.g., hsl) not handled here
    return null;
}
/** Compute `getComputedStyle` for arbitrary CSS color (used for `var()` in rgba helpers). */
function resolveCssColor(value) {
    const el = document.createElement('span');
    el.style.color = value;
    document.body.appendChild(el);
    const color = getComputedStyle(el).color;
    document.body.removeChild(el);
    return color; // browser-normalized "rgb(...)" / "rgba(...)"
}
/** Parse `rgb(...)` string to `rgba(..., alpha)`. */
function rgbStringToRgba(rgb, alpha) {
    const nums = rgb.match(/[\d.]+/g);
    if (!nums || nums.length < 3)
        throw new Error('Invalid rgb color');
    const [r, g, b] = nums.map(Number);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
/** 8-digit hex with alpha byte; resolves `var()` via `resolveCssColor` (promo cards, top sections). */
export function hexWithOpacity(hex, opacityPercent) {
    if (!hex)
        throw new Error('Hex required');
    const alpha = Math.round((opacityPercent / 100) * 255)
        .toString(16)
        .padStart(2, '0');
    if (hex.startsWith('var(')) {
        const rgb = resolveCssColor(hex);
        return rgbStringToRgba(rgb, opacityPercent / 100);
    }
    const cleanHex = hex.replace('#', '');
    const fullHex = cleanHex.length === 3
        ? cleanHex
            .split('')
            .map((c) => c + c)
            .join('')
        : cleanHex;
    if (fullHex.length !== 6) {
        throw new Error('Invalid hex');
    }
    return `#${fullHex}${alpha}`;
}
/** `rgba()` from hex or `var()` with 0–100% opacity (promotions / casino cards). */
export function hexToRgba(hex, opacityPercent) {
    if (!hex)
        throw new Error('Hex required');
    const alpha = Math.max(0, Math.min(1, opacityPercent / 100));
    // CSS var()
    if (hex.startsWith('var(')) {
        const rgb = resolveCssColor(hex);
        return rgbStringToRgba(rgb, alpha);
    }
    const cleanHex = hex.replace('#', '');
    const fullHex = cleanHex.length === 3
        ? cleanHex
            .split('')
            .map((c) => c + c)
            .join('')
        : cleanHex;
    if (fullHex.length !== 6) {
        throw new Error('Invalid hex');
    }
    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
/** Provider slug → path prefix (`/p/` vs `/` for sports) for game/profile links. */
export const providerBasePath = (provider) => {
    return `${provider?.toLowerCase() === 'sports' ? '/' : '/p/'}`;
};
// RxJS (FOMO bets)
/** Non-Svelte bridge: BetBy frame collapses FOMO bet feed when betslip opens; mobile menu reads observable. */
const isCollapsedFOMOBets = getBehaviorSubject(false);
export const getIsCollapsedFOMOBets = () => isCollapsedFOMOBets.asObservable();
export const setIsCollapsedFomoBets = (v) => isCollapsedFOMOBets.next(v);
// UTC
/** Parse API/CMS datetime to `Date` treating bare ISO as UTC (advent calendar, quests). */
export function parseUtcDate(value) {
    if (!value)
        return new Date();
    if (value instanceof Date)
        return value;
    if (/([zZ]|[+\-]\d{2}:?\d{2})$/.test(value)) {
        return new Date(value);
    }
    return new Date(value + 'Z');
}
/** Epoch ms from string/Date/number; bare ISO without offset → UTC (sidebar races, timers). */
export function parseUtcTimestamp(input) {
    if (input == null)
        return NaN;
    let iso;
    if (typeof input === 'string') {
        iso = input;
    }
    else {
        const d = input instanceof Date ? input : new Date(input);
        if (isNaN(d.getTime()))
            return NaN;
        iso = d.toISOString();
    }
    if (/[zZ]|[+\-]\d{2}:\d{2}$/.test(iso))
        return new Date(iso).getTime();
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/);
    if (!m)
        return new Date(iso + 'Z').getTime();
    const [, Y, M, D, h, mn, ss = '0', frac = '0'] = m;
    const ms = frac.padEnd(3, '0').slice(0, 3);
    return Date.UTC(+Y, +M - 1, +D, +h, +mn, +ss, Number(ms));
}
